/* eslint-disable react/prop-types */
/**
 * Cash ladder — the "what if" half.
 *
 * The ladder above answers one question with its assumptions fixed. This lets
 * you move the assumptions that actually change the answer and see the walk
 * redraw: how fine a resolution (month / week / DAY, so a single expensive
 * departure can be pinned to its date), when we book with the airline, whether
 * new bookings keep arriving, and whether the trips currently failing go on to
 * default.
 *
 * Every scenario is a POST to /api/admin/cash-ladder/simulate, which is pure
 * arithmetic over primitives the last refresh already stored — no Stripe, no
 * ladder rebuild. That is why the toggles are instant and why this is a
 * separate endpoint from Refresh rather than a parameter on it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, TrendingDown, Sparkles, UserMinus, CalendarClock, Plus, X, PiggyBank } from 'lucide-react';

const usd = (cents) => {
  const v = (Number(cents) || 0) / 100;
  const s = Math.abs(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return v < 0 ? `-${s}` : s;
};
const usdExact = (cents) => {
  const v = (Number(cents) || 0) / 100;
  const s = Math.abs(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return v < 0 ? `-${s}` : s;
};
/** The simulate endpoint takes at most this many cash entries. */
const MAX_INJECTIONS = 10;
const pretty = (iso) => {
  if (!iso || iso === 'now') return 'Today';
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

const GRANULARITY = [
  { id: 'month', label: 'Month', horizon: 365 },
  { id: 'week', label: 'Week', horizon: 168 },
  { id: 'day', label: 'Day', horizon: 120 },
];

function Toggle({ on, onClick, icon: Icon, label, hint }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`flex items-start gap-2.5 rounded-xl border px-3 py-2.5 text-left transition ${
        on ? 'border-white/25 bg-white/10' : 'border-white/10 bg-white/[0.03] hover:bg-white/[0.06]'
      }`}
    >
      <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${on ? 'text-white' : 'text-white/40'}`} strokeWidth={1.75} />
      <span className="min-w-0">
        <span className={`block text-[13.5px] leading-tight ${on ? 'text-white/90 font-medium' : 'text-white/70'}`}>{label}</span>
        <span className="block text-[12px] leading-snug text-white/45 mt-0.5">{hint}</span>
      </span>
    </button>
  );
}

function Knob({ label, value, onChange, min, max, step = 1, suffix, hint }) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-2">
        <span className="text-[12.5px] text-white/60">{label}</span>
        <span className="text-[13px] text-white/90 tabular-nums">{value}{suffix}</span>
      </span>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-1.5 accent-white/80"
      />
      {hint ? <span className="block text-[11.5px] text-white/40 leading-snug">{hint}</span> : null}
    </label>
  );
}

export default function CashLadderSimulator({ authedFetch, openingCashCents = 0 }) {
  const [granularity, setGranularity] = useState('week');
  const [horizonDays, setHorizonDays] = useState(84);
  /** The ladder's own rule: a ticket just has to exist before the trip leaves. */
  const [bookDays, setBookDays] = useState(1);
  const [newBookings, setNewBookings] = useState(false);
  const [perWeek, setPerWeek] = useState(16);
  const [completion, setCompletion] = useState(41);
  const [defaults, setDefaults] = useState(false);
  const [useCash, setUseCash] = useState(false);
  /** Imaginary cash: what would it take to keep every day out of the red? */
  const [injections, setInjections] = useState([]);
  const [injectionsSent, setInjectionsSent] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(() => new Set());

  // Typing an amount re-runs once the typing stops, not on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setInjectionsSent(
      injections
        .map((i) => ({ date: i.date, amount_cents: Math.round((parseFloat(String(i.amount).replace(/[$,\s]/g, '')) || 0) * 100) }))
        .filter((i) => /^\d{4}-\d{2}-\d{2}$/.test(i.date) && i.amount_cents > 0),
    ), 350);
    return () => clearTimeout(t);
  }, [injections]);

  /** The simulator's own today (its first row, in UTC), never the browser's
   *  local date: ahead of UTC, "today" would land tomorrow in the engine. */
  const simToday = () => data?.result?.rows?.[0]?.start || new Date().toISOString().slice(0, 10);
  const addInjection = (amount = '', date = simToday()) =>
    setInjections((list) => (list.length >= MAX_INJECTIONS ? list
      : [...list, { id: `${Date.now()}-${list.length}`, amount, date }]));
  /** "Try it": the needed cash, on the day it's needed. An entry already on
   *  that day takes it (so the 10-entry limit never strands it). */
  const addNeeded = (cents, day) => setInjections((list) => {
    const date = day && day !== 'now' ? day : simToday();
    const same = list.find((i) => i.date === date);
    const amountOf = (i) => parseFloat(String(i.amount).replace(/[$,\s]/g, '')) || 0;
    if (same) return list.map((i) => (i === same ? { ...i, amount: (amountOf(i) + cents / 100).toFixed(2) } : i));
    if (list.length >= MAX_INJECTIONS) return list;
    return [...list, { id: `${Date.now()}-${list.length}`, amount: (cents / 100).toFixed(2), date }];
  });
  const updateInjection = (id, patch) => setInjections((list) => list.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  const removeInjection = (id) => setInjections((list) => list.filter((i) => i.id !== id));

  const run = useCallback(async () => {
    try {
      setBusy(true); setErr(null);
      const json = await authedFetch('admin/cash-ladder/simulate', {
        method: 'POST',
        body: JSON.stringify({
          granularity,
          horizon_days: horizonDays,
          opening_cash_cents: useCash ? openingCashCents : 0,
          book_days_before_departure: bookDays,
          include_new_bookings: newBookings,
          new_bookings_per_week: perWeek,
          plan_completion_share: completion / 100,
          defaults_mode: defaults ? 'failing' : 'none',
          injections: injectionsSent,
        }),
      });
      setData(json);
    } catch (e) {
      setErr(e?.message || 'Simulation failed.');
      setData(null);
    } finally {
      setBusy(false);
    }
  }, [authedFetch, granularity, horizonDays, bookDays, newBookings, perWeek, completion, defaults, useCash, openingCashCents, injectionsSent]);

  useEffect(() => { run(); }, [run]);

  const rows = useMemo(() => data?.result?.rows || [], [data]);
  const s = data?.result?.summary;
  const worst = useMemo(
    () => [...rows].filter((r) => r.outlayCents > 0).sort((a, b) => b.outlayCents - a.outlayCents).slice(0, 3).map((r) => r.period),
    [rows],
  );

  const toggleRow = (p) => setOpen((prev) => { const n = new Set(prev); if (n.has(p)) n.delete(p); else n.add(p); return n; });

  return (
    <section className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
      <header className="px-4 md:px-5 py-4 border-b border-white/10">
        <h2 className="text-[19px] font-semibold text-white/90">Simulator</h2>
        <p className="text-[13px] text-white/50 mt-1 leading-snug">
          Move the assumptions and watch the walk redraw. Runs on the stored snapshot, so nothing here costs a Stripe call.
        </p>
      </header>

      <div className="px-4 md:px-5 py-4 space-y-4 border-b border-white/10">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-white/10 bg-white/[0.03] p-0.5">
            {GRANULARITY.map((g) => (
              <button
                key={g.id} type="button"
                onClick={() => { setGranularity(g.id); setHorizonDays(Math.min(horizonDays, g.horizon)); }}
                className={`px-3 py-1.5 rounded-md text-[13px] transition ${
                  granularity === g.id ? 'bg-white text-zinc-950 font-medium' : 'text-white/60 hover:text-white/90'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          <span className="text-[12.5px] text-white/40">
            {granularity === 'day' ? 'one row per day — pinpoints the exact departure that creates a gap' : `${rows.length - 1} periods`}
          </span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Toggle
            on={newBookings} onClick={() => setNewBookings((v) => !v)} icon={Sparkles}
            label="Include new bookings"
            hint={`${perWeek}/week keeps arriving, each paying in and billing airfare later`}
          />
          <Toggle
            on={defaults} onClick={() => setDefaults((v) => !v)} icon={UserMinus}
            label="Assume failing trips default"
            hint="They stop paying — and we never buy their tickets"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 pt-1">
          <Knob label="Horizon" value={horizonDays} onChange={setHorizonDays} min={14}
                max={GRANULARITY.find((g) => g.id === granularity).horizon} step={7} suffix=" days" />
          <Knob label="Book this far before departure" value={bookDays} onChange={setBookDays} min={0} max={60} suffix=" days"
                hint="Deadline is 1 (the day before). Today we book a median 22 out. A trip that pays off sooner is ticketed at payoff" />
          {newBookings ? (
            <Knob label="New bookings" value={perWeek} onChange={setPerWeek} min={0} max={40} suffix="/wk" hint="8-week average is 16" />
          ) : <span className="hidden lg:block" />}
          {newBookings ? (
            <Knob label="Plans paid off by their end date" value={completion} onChange={setCompletion} min={20} max={100} suffix="%"
                  hint="Measured: 41%" />
          ) : <span className="hidden lg:block" />}
        </div>

        {openingCashCents > 0 ? (
          <label className="flex items-center gap-2 text-[13px] text-white/60">
            <input type="checkbox" checked={useCash} onChange={(e) => setUseCash(e.target.checked)} className="accent-white/80" />
            Start from the cash on file ({usdExact(openingCashCents)}) instead of zero
          </label>
        ) : null}

        <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-3" data-testid="sim-injections">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-2 text-[13px] text-white/70">
              <PiggyBank className="w-4 h-4 text-white/45" strokeWidth={1.75} />
              Imaginary cash
            </span>
            <button
              type="button" onClick={() => addInjection()} disabled={injections.length >= MAX_INJECTIONS}
              className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-2.5 py-1 text-[12.5px] text-white/70 hover:bg-white/[0.06] disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <Plus className="w-3.5 h-3.5" /> Add cash
            </button>
          </div>
          {injections.length === 0 ? (
            <p className="text-[12px] text-white/40 mt-1.5 leading-snug">Try an amount on a date and the walk redraws as if it landed then. Nothing is saved.</p>
          ) : (
            <ul className="mt-2 space-y-2">
              {injections.map((i) => (
                <li key={i.id} className="flex items-center gap-2">
                  <span className="text-white/45 text-[13px]">$</span>
                  <input
                    type="text" inputMode="decimal" placeholder="Amount" value={i.amount}
                    onChange={(e) => updateInjection(i.id, { amount: e.target.value })}
                    aria-label="Amount to add"
                    className="w-28 rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-[13px] text-white/90 tabular-nums outline-none focus:border-white/25"
                  />
                  <span className="text-white/40 text-[12.5px]">on</span>
                  <input
                    type="date" value={i.date}
                    onChange={(e) => updateInjection(i.id, { date: e.target.value })}
                    aria-label="Day it lands"
                    className="rounded-lg border border-white/10 bg-white/[0.04] px-2.5 py-1.5 text-[13px] text-white/90 outline-none focus:border-white/25 [color-scheme:dark]"
                  />
                  <button type="button" onClick={() => removeInjection(i.id)} aria-label="Remove" className="p-1 text-white/40 hover:text-white/80">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          {injections.length >= MAX_INJECTIONS ? (
            <p className="text-[12px] text-white/45 mt-2">Up to {MAX_INJECTIONS} entries. Change an amount or remove one to add another.</p>
          ) : null}
        </div>
      </div>

      {err ? <p className="px-4 md:px-5 py-4 text-[13px] text-red-300/90">{err}</p> : null}

      {s ? (
        <div className="px-4 md:px-5 py-3 border-b border-white/10 flex flex-wrap items-center justify-between gap-2" data-testid="sim-cash-needed">
          {s.cashNeededCents > 0 ? (
            <>
              <p className="text-[13.5px] text-white/80 leading-snug">
                Cash needed to stay above $0: <span className="text-red-300 tabular-nums">{usdExact(s.cashNeededCents)}</span>
                {' '}by {s.cashNeededBy === 'now' ? 'today' : pretty(s.cashNeededBy)}
                {s.injectedCents > 0 ? <span className="text-white/45"> (on top of the {usdExact(s.injectedCents)} added)</span> : null}
              </p>
              {(() => {
                const day = s.cashNeededBy && s.cashNeededBy !== 'now' ? s.cashNeededBy : simToday();
                const blocked = injections.length >= MAX_INJECTIONS && !injections.some((i) => i.date === day);
                return (
                  <button
                    type="button" disabled={blocked}
                    title={blocked ? `Up to ${MAX_INJECTIONS} entries: remove one first` : undefined}
                    onClick={() => addNeeded(s.cashNeededCents, s.cashNeededBy)}
                    className="rounded-lg border border-white/15 px-2.5 py-1 text-[12.5px] text-white/80 hover:bg-white/[0.06] disabled:opacity-40"
                  >
                    Try it
                  </button>
                );
              })()}
            </>
          ) : (
            <p className="text-[13.5px] text-emerald-300/85">
              {s.injectedCents > 0
                ? `Covered: with ${usdExact(s.injectedCents)} added, every day stays at or above $0.`
                : 'Every day stays at or above $0.'}
            </p>
          )}
        </div>
      ) : null}

      {s ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border-b border-white/10">
          {[
            { k: 'Deposits in', v: usdExact(s.totalDepositCents) },
            { k: 'Airfare out', v: usdExact(s.totalOutlayCents) },
            { k: 'Ends at', v: usdExact(s.endingCents), bad: s.endingCents < 0 },
            { k: 'Lowest point', v: `${usdExact(s.lowPointCents)}`, sub: pretty(s.lowPointAt), bad: s.lowPointCents < 0 },
          ].map((c) => (
            <div key={c.k} className="bg-zinc-950/60 px-4 py-3">
              <div className="text-[11.5px] uppercase tracking-wide text-white/40">{c.k}</div>
              <div className={`text-[17px] tabular-nums mt-0.5 ${c.bad ? 'text-red-300' : 'text-white/90'}`}>{c.v}</div>
              {c.sub ? <div className="text-[12px] text-white/45">{c.sub}</div> : null}
            </div>
          ))}
        </div>
      ) : null}

      {s && defaults ? (
        <p className="px-4 md:px-5 py-3 text-[12.5px] text-white/55 border-b border-white/10 leading-snug">
          Defaults remove {usdExact(s.defaultsExcludedDepositCents)} of payments and {usdExact(s.defaultsAvoidedFareCents)} of
          airfare{s.defaultsAvoidedInsuranceCents > 0
            ? <> plus {usdExact(s.defaultsAvoidedInsuranceCents)} of Faye policies never bought</>
            : null} — a net {s.defaultsNetCents >= 0 ? 'gain' : 'cost'} of {usdExact(Math.abs(s.defaultsNetCents))}.
        </p>
      ) : null}

      <div className={`transition-opacity ${busy ? 'opacity-50' : ''}`}>
        {rows.map((r) => {
          const isOpen = open.has(r.period);
          const hot = worst.includes(r.period);
          return (
            <div key={r.period} className="border-b border-white/[0.06] last:border-0">
              <button
                type="button"
                onClick={() => r.wires?.length && toggleRow(r.period)}
                className={`w-full grid grid-cols-[1fr_auto] md:grid-cols-[minmax(90px,1fr)_repeat(3,minmax(88px,auto))_72px] items-center gap-x-3 gap-y-1 px-4 md:px-5 py-2.5 text-left ${
                  r.wires?.length ? 'hover:bg-white/[0.04]' : 'cursor-default'
                }`}
              >
                <span className="flex items-center gap-1.5 min-w-0">
                  {r.wires?.length ? (
                    isOpen ? <ChevronDown className="w-3.5 h-3.5 text-white/35 shrink-0" />
                           : <ChevronRight className="w-3.5 h-3.5 text-white/35 shrink-0" />
                  ) : <span className="w-3.5 shrink-0" />}
                  <span className="text-[13.5px] text-white/80 truncate">{pretty(r.period)}</span>
                  {hot ? <CalendarClock className="w-3.5 h-3.5 text-amber-300/70 shrink-0" /> : null}
                  {r.injectionCents > 0 ? (
                    <span className="text-[12px] text-emerald-300/80 tabular-nums shrink-0">+{usd(r.injectionCents)} added</span>
                  ) : null}
                </span>
                <span className="hidden md:block text-[13.5px] tabular-nums text-emerald-300/80 text-right">
                  {r.depositCents ? usd(r.depositCents) : '—'}
                </span>
                <span className="hidden md:block text-[13.5px] tabular-nums text-white/60 text-right">
                  {r.outlayCents ? usd(r.outlayCents) : '—'}
                </span>
                <span className={`text-[13.5px] tabular-nums text-right ${r.balanceCents < 0 ? 'text-red-300' : 'text-white/90'}`}>
                  {usd(r.balanceCents)}
                </span>
                <span className="hidden md:block text-[12px] tabular-nums text-white/35 text-right">
                  {r.cover == null ? '' : `${r.cover}x`}
                </span>
                <span className="md:hidden col-span-2 text-[12px] text-white/45 tabular-nums">
                  in {usd(r.depositCents)} · out {usd(r.outlayCents)}
                </span>
              </button>
              {isOpen && r.wires?.length ? (
                <ul className="px-4 md:px-5 pb-3 pl-9 md:pl-11 space-y-1">
                  {r.wires.map((w, i) => (
                    <li key={`${w.ref}-${i}`} className="flex items-baseline justify-between gap-3 text-[12.5px]">
                      <span className="text-white/60 truncate">
                        {w.ref}
                        {w.trigger === 'paid_off' ? <span className="text-rose-200/70"> · paid off</span> : null}
                        {w.trigger === 'payoff' ? <span className="text-white/45"> · pays off</span> : null}
                        {w.departureDay ? <span className="text-white/35"> · departs {pretty(w.departureDay)}</span> : null}
                      </span>
                      <span className="tabular-nums text-white/70 shrink-0">{usdExact(w.cents)}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>

      {s?.firstGapAt ? (
        <p className="px-4 md:px-5 py-3 flex items-start gap-2 text-[12.5px] text-white/55 border-t border-white/10">
          <TrendingDown className="w-3.5 h-3.5 mt-0.5 shrink-0 text-red-300/70" />
          <span>
            Goes negative at {pretty(s.firstGapAt)} and stays under water for {s.gapPeriods} of {rows.length} periods.
            {s.overdueOutlayCents ? ` ${usdExact(s.overdueOutlayCents)} of that is airfare already overdue today.` : ''}
          </span>
        </p>
      ) : null}
    </section>
  );
}
